import { useEffect, useState } from "react";
import { FaSpinner } from "react-icons/fa";
import Button from "../../componentes/button/button";
import { salvarBlob } from "../../services/resource/tiInventarioService";

/**
 * Tipos exibíveis no navegador, por extensão. O tipo MIME é definido aqui (e não pelo que o
 * servidor informa) para que o conteúdo só seja interpretado como mídia/PDF/texto, nunca como HTML.
 */
const FORMATOS = {
    png: { tipo: "imagem", mime: "image/png" },
    jpg: { tipo: "imagem", mime: "image/jpeg" },
    jpeg: { tipo: "imagem", mime: "image/jpeg" },
    gif: { tipo: "imagem", mime: "image/gif" },
    webp: { tipo: "imagem", mime: "image/webp" },
    bmp: { tipo: "imagem", mime: "image/bmp" },
    pdf: { tipo: "pdf", mime: "application/pdf" },
    mp4: { tipo: "video", mime: "video/mp4" },
    webm: { tipo: "video", mime: "video/webm" },
    mov: { tipo: "video", mime: "video/mp4" },
    mp3: { tipo: "audio", mime: "audio/mpeg" },
    wav: { tipo: "audio", mime: "audio/wav" },
    ogg: { tipo: "audio", mime: "audio/ogg" },
    txt: { tipo: "texto", mime: "text/plain" },
    csv: { tipo: "texto", mime: "text/plain" },
};

const LIMITE_TEXTO = 200000;

export function extensaoDe(nome = "") {
    const i = nome.lastIndexOf(".");
    return i >= 0 ? nome.slice(i + 1).toLowerCase() : "";
}

export function podeVisualizar(anexo) {
    return !!FORMATOS[extensaoDe(anexo?.nomeOriginal)];
}

/** Painel de visualização de um anexo: imagem, PDF, vídeo, áudio e texto; demais tipos só para download. */
export default function VisualizadorAnexo({ service, anexo }) {
    const [estado, setEstado] = useState({ carregando: false, url: null, texto: null, truncado: false, blob: null, erro: false });

    useEffect(() => {
        let ativo = true;
        let urlCriada = null;
        setEstado({ carregando: false, url: null, texto: null, truncado: false, blob: null, erro: false });
        if (!anexo) return undefined;

        const formato = FORMATOS[extensaoDe(anexo.nomeOriginal)];
        if (!formato) return undefined;

        setEstado(prev => ({ ...prev, carregando: true }));
        service.baixarAnexo(anexo.id)
            .then(async (res) => {
                if (!ativo) return;
                const blob = new Blob([res.data], { type: formato.mime });
                if (formato.tipo === "texto") {
                    const completo = await blob.text();
                    if (!ativo) return;
                    setEstado({
                        carregando: false, url: null, blob,
                        texto: completo.slice(0, LIMITE_TEXTO), truncado: completo.length > LIMITE_TEXTO, erro: false,
                    });
                    return;
                }
                urlCriada = URL.createObjectURL(blob);
                setEstado({ carregando: false, url: urlCriada, texto: null, truncado: false, blob, erro: false });
            })
            .catch(() => {
                if (ativo) setEstado({ carregando: false, url: null, texto: null, truncado: false, blob: null, erro: true });
            });

        return () => {
            ativo = false;
            if (urlCriada) URL.revokeObjectURL(urlCriada);
        };
    }, [anexo, service]);

    if (!anexo) {
        return (
            <div className="h-full min-h-[260px] flex items-center justify-center text-sm text-muted-foreground text-center p-6">
                Selecione um documento da lista para visualizar.
            </div>
        );
    }

    const formato = FORMATOS[extensaoDe(anexo.nomeOriginal)];
    const baixar = async () => {
        if (estado.blob) {
            salvarBlob({ data: estado.blob }, anexo.nomeOriginal);
            return;
        }
        const res = await service.baixarAnexo(anexo.id);
        salvarBlob(res, anexo.nomeOriginal);
    };

    let conteudo;
    if (!formato) {
        conteudo = (
            <div className="flex flex-col items-center justify-center gap-3 min-h-[260px] text-sm text-muted-foreground text-center p-6">
                <i className="fa fa-file text-4xl" />
                <p>Este tipo de arquivo (.{extensaoDe(anexo.nomeOriginal) || "?"}) não pode ser visualizado no navegador.</p>
                <Button label="Baixar arquivo" iconLeft="fa fa-download" variant="indigo" onClick={baixar} />
            </div>
        );
    } else if (estado.carregando) {
        conteudo = (
            <div className="flex items-center justify-center gap-2 min-h-[260px] text-muted-foreground">
                <FaSpinner className="animate-spin" size={18} /> <span className="text-sm">Carregando...</span>
            </div>
        );
    } else if (estado.erro) {
        conteudo = (
            <div className="flex items-center justify-center min-h-[260px] text-sm text-destructive">
                Não foi possível carregar o documento.
            </div>
        );
    } else if (formato.tipo === "imagem" && estado.url) {
        conteudo = (
            <div className="flex items-center justify-center bg-muted/40 rounded-lg p-2">
                <img src={estado.url} alt={anexo.nomeOriginal} className="max-h-[60vh] max-w-full object-contain" />
            </div>
        );
    } else if (formato.tipo === "pdf" && estado.url) {
        conteudo = <iframe title={anexo.nomeOriginal} src={estado.url} className="w-full h-[60vh] rounded-lg border border-border" />;
    } else if (formato.tipo === "video" && estado.url) {
        conteudo = (
            <video src={estado.url} controls className="w-full max-h-[60vh] rounded-lg bg-black">
                Seu navegador não suporta a reprodução deste vídeo.
            </video>
        );
    } else if (formato.tipo === "audio" && estado.url) {
        conteudo = (
            <div className="flex items-center justify-center min-h-[120px]">
                <audio src={estado.url} controls className="w-full" />
            </div>
        );
    } else if (formato.tipo === "texto" && estado.texto !== null) {
        conteudo = (
            <>
                <pre className="text-xs whitespace-pre-wrap break-words max-h-[60vh] overflow-auto rounded-lg border border-border bg-muted/30 p-3">
                    {estado.texto}
                </pre>
                {estado.truncado && (
                    <p className="text-xs text-muted-foreground mt-1">Exibindo apenas o início do arquivo. Baixe-o para ver o conteúdo completo.</p>
                )}
            </>
        );
    } else {
        conteudo = null;
    }

    return (
        <div>
            <div className="flex flex-wrap items-center justify-between gap-2 mb-2">
                <p className="text-sm font-medium text-foreground break-all">{anexo.nomeOriginal}</p>
                <div className="flex gap-2">
                    {estado.url && (
                        <Button label="Abrir em nova aba" iconLeft="fa fa-external-link-alt" variant="white"
                            onClick={() => window.open(estado.url, "_blank", "noopener")} />
                    )}
                    <Button label="Baixar" iconLeft="fa fa-download" variant="white" onClick={baixar} />
                </div>
            </div>
            {conteudo}
        </div>
    );
}
